import { useState, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { PlanTier, usePermissions } from "../utils/permissions";
import {
  isDuplicateName,
  sanitizeAndTrim,
  cleanEmptyFields,
  validateName,
} from "../utils/fieldUtils";

export type ViewType = "folders" | "folder" | "field";

export interface UseFolderFieldSpaceOptions {
  user: any;
  reservedFolderKeys?: string[];
  translationPrefix: string;
}

export interface UseFolderFieldSpaceReturn {
  updatedData: Record<string, any>;
  setUpdatedData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  isNewKeyProcess: boolean;
  setIsNewKeyProcess: React.Dispatch<React.SetStateAction<boolean>>;
  selectOptionValue: string;
  setSelectOptionValue: React.Dispatch<React.SetStateAction<string>>;
  showConfirm: boolean;
  setShowConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  editorMode: "simple" | "advanced";
  setEditorMode: React.Dispatch<React.SetStateAction<"simple" | "advanced">>;
  currentView: ViewType;
  setCurrentView: React.Dispatch<React.SetStateAction<ViewType>>;
  selectedFolder: string;
  setSelectedFolder: React.Dispatch<React.SetStateAction<string>>;
  selectedField: string;
  setSelectedField: React.Dispatch<React.SetStateAction<string>>;
  isNewFieldProcess: boolean;
  setIsNewFieldProcess: React.Dispatch<React.SetStateAction<boolean>>;
  showDeleteFieldConfirm: boolean;
  setShowDeleteFieldConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  showFieldLimitModal: boolean;
  setShowFieldLimitModal: React.Dispatch<React.SetStateAction<boolean>>;
  folderFieldCounts: Record<string, number>;
  setFolderFieldCounts: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;

  control: any;
  watch: any;
  setValue: any;
  inputTextValue: string;

  tempFieldNameRef: React.MutableRefObject<string>;
  originalFieldDataRef: React.MutableRefObject<Record<string, any>>;

  maxLimit: number;
  targetUpgradeTier: PlanTier;
  premiumTierUser: boolean;
  enterpriseTierUser: boolean;
  largeBusinessTierUser: boolean;

  handleOpenFolder: (folderKey: string) => void;
  handleBackToFolders: () => void;
  handleOpenField: (fieldKey: string) => void;
  handleBackToFolder: () => void;
  handleCreateNewFolder: (existingFolders: string[]) => boolean;
  handleCreateNewField: (existingFolders?: string[]) => boolean;
  handleFieldChange: (
    newValue: Record<string, any>,
    isNewField: boolean
  ) => void;
  validateFolderName: (
    folderName: string,
    existingFolders: string[]
  ) => boolean;
  validateFieldName: (
    fieldName: string,
    existingFields: string[],
    excludeField?: string
  ) => boolean;
  cleanAndPrepareData: (fieldData: Record<string, any>) => Record<string, any>;
  sanitizeInput: (value: string) => string;
  isReservedFolder: (folderKey: string) => boolean;
}

export const useFolderFieldSpace = ({
  user,
  reservedFolderKeys = [],
  translationPrefix,
}: UseFolderFieldSpaceOptions): UseFolderFieldSpaceReturn => {
  const { t } = useTranslation();

  const { control, watch, setValue } = useForm({
    defaultValues: {
      newKeyName: "",
    },
  });

  const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
  const [isNewKeyProcess, setIsNewKeyProcess] = useState<boolean>(false);
  const [selectOptionValue, setSelectOptionValue] = useState<string>("");
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [editorMode, setEditorMode] = useState<"simple" | "advanced">("simple");
  const [currentView, setCurrentView] = useState<ViewType>("folders");
  const [selectedFolder, setSelectedFolder] = useState<string>("");
  const [selectedField, setSelectedField] = useState<string>("");
  const [isNewFieldProcess, setIsNewFieldProcess] = useState<boolean>(false);
  const [showDeleteFieldConfirm, setShowDeleteFieldConfirm] =
    useState<boolean>(false);
  const [showFieldLimitModal, setShowFieldLimitModal] = useState(false);
  const [folderFieldCounts, setFolderFieldCounts] = useState<
    Record<string, number>
  >({});

  const tempFieldNameRef = useRef<string>("");
  const originalFieldDataRef = useRef<Record<string, any>>({});

  const permissions = usePermissions(user);
  const premiumTierUser = permissions.isAtLeastTier(PlanTier.PREMIUM);
  const enterpriseTierUser = permissions.isAtLeastTier(PlanTier.ENTERPRISE);
  const largeBusinessTierUser = permissions.isAtLeastTier(
    PlanTier.LARGE_BUSINESS
  );

  const maxLimit = largeBusinessTierUser
    ? Infinity
    : enterpriseTierUser
    ? 10
    : premiumTierUser
    ? 5
    : 2;
  const targetUpgradeTier = !premiumTierUser
    ? PlanTier.PREMIUM
    : enterpriseTierUser
    ? PlanTier.LARGE_BUSINESS
    : PlanTier.ENTERPRISE;

  const inputTextValue = watch("newKeyName");

  const isReservedFolder = useCallback(
    (folderKey: string) => {
      return reservedFolderKeys.includes(folderKey);
    },
    [reservedFolderKeys]
  );

  const sanitizeInput = useCallback((value: string) => {
    return sanitizeAndTrim(value);
  }, []);

  const validateFolderName = useCallback(
    (folderName: string, existingFolders: string[]): boolean => {
      const nameValidation = validateName(folderName);
      if (!nameValidation.isValid) {
        const errorMessage = nameValidation.errorKey
          ? t(nameValidation.errorKey, nameValidation.errorParams || {})
          : "Invalid folder name";
        toast.error(errorMessage);
        return false;
      }

      if (reservedFolderKeys.includes(folderName)) {
        toast.error(t(`${translationPrefix}.reservedFolderNameError`));
        return false;
      }
      if (isDuplicateName(folderName, existingFolders)) {
        toast.error(t(`${translationPrefix}.duplicateFolderNameError`));
        return false;
      }
      return true;
    },
    [reservedFolderKeys, t, translationPrefix]
  );

  const validateFieldName = useCallback(
    (
      fieldName: string,
      existingFields: string[],
      excludeField?: string
    ): boolean => {
      if (isDuplicateName(fieldName, existingFields, excludeField)) {
        toast.error(t(`${translationPrefix}.duplicateFieldNameError`));
        return false;
      }
      return true;
    },
    [t, translationPrefix]
  );

  const cleanAndPrepareData = useCallback(
    (fieldData: Record<string, any>): Record<string, any> => {
      return cleanEmptyFields(fieldData);
    },
    []
  );

  const handleOpenFolder = useCallback((folderKey: string) => {
    setSelectedFolder(folderKey);
    setSelectOptionValue(folderKey);
    setCurrentView("folder");
    setIsNewKeyProcess(false);
  }, []);

  const handleBackToFolders = useCallback(() => {
    setCurrentView("folders");
    setSelectedFolder("");
    setSelectOptionValue("");
    setIsNewKeyProcess(false);
    setValue("newKeyName", "");
    setUpdatedData({});
    setEditorMode("simple");
    setSelectedField("");
    setIsNewFieldProcess(false);
  }, [setValue]);

  const handleOpenField = useCallback(
    (fieldKey: string) => {
      originalFieldDataRef.current = JSON.parse(JSON.stringify(updatedData));
      setSelectedField(fieldKey);
      setCurrentView("field");
      setIsNewFieldProcess(false);
    },
    [updatedData]
  );

  const handleBackToFolder = useCallback(() => {
    if (isNewFieldProcess && tempFieldNameRef.current) {
      const newData = { ...updatedData };
      delete newData[tempFieldNameRef.current];
      setUpdatedData(newData);
      tempFieldNameRef.current = "";
    } else if (
      !isNewFieldProcess &&
      Object.keys(originalFieldDataRef.current).length > 0
    ) {
      setUpdatedData(originalFieldDataRef.current);
      originalFieldDataRef.current = {};
    }

    setCurrentView("folder");
    setSelectedField("");
    setIsNewFieldProcess(false);
  }, [isNewFieldProcess, updatedData]);

  const handleCreateNewFolder = useCallback(
    (existingFolders: string[]): boolean => {
      const countableFolders = existingFolders.filter(
        (key) => !reservedFolderKeys.includes(key)
      );
      if (countableFolders.length >= maxLimit) {
        if (!largeBusinessTierUser) {
          setShowFieldLimitModal(true);
        }
        return false;
      }
      setIsNewKeyProcess(true);
      setCurrentView("folder");
      setSelectedFolder("");
      setSelectOptionValue("");
      setValue("newKeyName", "");
      setUpdatedData({});
      return true;
    },
    [reservedFolderKeys, maxLimit, largeBusinessTierUser, setValue]
  );

  const handleCreateNewField = useCallback(
    (existingFolders?: string[]): boolean => {
      if (isNewKeyProcess && inputTextValue && existingFolders) {
        if (!validateFolderName(inputTextValue, existingFolders)) {
          return false;
        }
      }

      if (Object.keys(updatedData).length >= maxLimit) {
        if (!largeBusinessTierUser) {
          setShowFieldLimitModal(true);
        }
        return false;
      }
      tempFieldNameRef.current = "";
      setIsNewFieldProcess(true);
      setCurrentView("field");
      setSelectedField("");
      return true;
    },
    [
      isNewKeyProcess,
      inputTextValue,
      updatedData,
      maxLimit,
      largeBusinessTierUser,
      validateFolderName,
    ]
  );

  const handleFieldChange = useCallback(
    (newValue: Record<string, any>, isNewField: boolean) => {
      if (isNewField) {
        const newData = { ...updatedData };
        const newFieldKey = Object.keys(newValue)[0];

        if (newFieldKey && newFieldKey !== tempFieldNameRef.current) {
          const existingFields = Object.keys(updatedData).filter(
            (key) => key !== tempFieldNameRef.current
          );
          if (!validateFieldName(newFieldKey, existingFields)) {
            return;
          }
        }

        if (
          tempFieldNameRef.current &&
          tempFieldNameRef.current !== newFieldKey
        ) {
          delete newData[tempFieldNameRef.current];
        }

        if (newFieldKey) {
          newData[newFieldKey] = newValue[newFieldKey];
          tempFieldNameRef.current = newFieldKey;
        }

        setUpdatedData(newData);
      } else {
        const newData = { ...updatedData };
        const newFieldKey = Object.keys(newValue)[0];

        if (!newFieldKey) {
          return;
        }

        if (newFieldKey !== selectedField) {
          const existingFields = Object.keys(updatedData).filter(
            (key) => key !== selectedField
          );
          if (!validateFieldName(newFieldKey, existingFields)) {
            return;
          }
          delete newData[selectedField];
          newData[newFieldKey] = newValue[newFieldKey];
          setSelectedField(newFieldKey);
        } else {
          newData[selectedField] = newValue[selectedField];
        }

        setUpdatedData(newData);
      }
    },
    [updatedData, selectedField, validateFieldName]
  );

  return {
    updatedData,
    setUpdatedData,
    isNewKeyProcess,
    setIsNewKeyProcess,
    selectOptionValue,
    setSelectOptionValue,
    showConfirm,
    setShowConfirm,
    editorMode,
    setEditorMode,
    currentView,
    setCurrentView,
    selectedFolder,
    setSelectedFolder,
    selectedField,
    setSelectedField,
    isNewFieldProcess,
    setIsNewFieldProcess,
    showDeleteFieldConfirm,
    setShowDeleteFieldConfirm,
    showFieldLimitModal,
    setShowFieldLimitModal,
    folderFieldCounts,
    setFolderFieldCounts,

    control,
    watch,
    setValue,
    inputTextValue,

    tempFieldNameRef,
    originalFieldDataRef,

    maxLimit,
    targetUpgradeTier,
    premiumTierUser,
    enterpriseTierUser,
    largeBusinessTierUser,

    handleOpenFolder,
    handleBackToFolders,
    handleOpenField,
    handleBackToFolder,
    handleCreateNewFolder,
    handleCreateNewField,
    handleFieldChange,
    validateFolderName,
    validateFieldName,
    cleanAndPrepareData,
    sanitizeInput,
    isReservedFolder,
  };
};
